import { inject, Injectable } from '@angular/core';
import { Apollo, gql, Mutation } from 'apollo-angular';
import { LoginResponse } from '../components/login/login.model';
import { BehaviorSubject, catchError, map, Observable, Subject } from 'rxjs';
import { User } from '../components/login/user.model';
import { Router } from '@angular/router';
import { JwtPayload, jwtDecode } from 'jwt-decode';
import { RefreshTokenService } from './refresh-token.service';
import { SocketService } from './socket.service';
import { FCMService } from './fcm.service';
import { UserService } from './user.service';
import { Store } from '@ngrx/store';
import { logout } from '../store/user/user.actions';
import { LogEventsEnum } from '../firebase.model';
import { Analytics, logEvent } from '@angular/fire/analytics';

const signUpGQL = gql`
  mutation SignUp($signUpInput: SignUpInput!) {
    signUp(input: $signUpInput) {
      _id
    }
  }
`;

// TODO (security): Both `_accessToken` and `_refreshToken` are written to / read
// from `localStorage.userData` throughout this service. Any XSS payload can
// exfiltrate both tokens, and there is no client-side mechanism to revoke them.
// Migration plan when the backend can support it:
//   1. Backend issues tokens as `Set-Cookie` with `HttpOnly; Secure; SameSite=Strict`.
//   2. Drop the `Authorization: Bearer` header in `auth-interceptor.service.ts`;
//      set `withCredentials: true` on the Apollo HttpLink + HttpClient instead.
//   3. Remove `localStorage.setItem/getItem/removeItem('userData', ...)` from this
//      file; replace the auto-login flow with a `/me`-style probe that returns
//      401 if the cookie is missing/expired.
//   4. `faqsAccessToken` (native-mobile webview handoff) needs its own design —
//      cookies can't cross app/web origins easily.
@Injectable({
  providedIn: 'root',
})
export class AuthService extends Mutation<LoginResponse> {
  private analytics = inject(Analytics);
  private router = inject(Router);
  private fcmService = inject(FCMService);
  private refreshTokenService = inject(RefreshTokenService);
  private socketService = inject(SocketService);
  private userService = inject(UserService);
  private store = inject(Store);
  user = new BehaviorSubject<User>(null);
  private tokenExpirationTimer: any;

  constructor(apollo: Apollo) {
    super(apollo);
    // Socket session revoked (server auth_version bump) or the socket-ticket
    // mutation came back UNAUTHENTICATED: silently refresh the access token and
    // reconnect. refreshToken() itself logs out when the refresh token is dead,
    // so no explicit failure branch is needed here.
    this.socketService.sessionRefreshRequired$.subscribe(() => {
      this.refreshToken().then((refreshed) => {
        if (refreshed) {
          this.socketService.reconnect();
        }
      });
    });
  }

  document = gql`
    mutation Login($loginInput: LoginInput!) {
      login(loginInput: $loginInput) {
        access_token
        refresh_token
      }
    }
  `;

  private getExpirationTime(token: string): number | null {
    try {
      const decodedToken: { exp?: number } = jwtDecode<JwtPayload>(token);
      if (decodedToken && typeof decodedToken.exp === 'number') {
        return decodedToken.exp;
      }
    } catch (error) {
      console.error('Error decoding token', error);
      return null;
    }
    return null;
  }

  private getId(token: string): string | null {
    try {
      const decodedToken: { sub?: string } = jwtDecode<JwtPayload>(token);
      if (decodedToken && typeof decodedToken.sub === 'string') {
        return decodedToken.sub;
      }
    } catch (error) {
      console.error('Error decoding token', error);
      return null;
    }
    return null;
  }

  handleAuthentication(
    email: string,
    accessToken: string,
    refreshToken: string
  ) {
    const accessTokenExpirationDate = new Date(
      this.getExpirationTime(accessToken) * 1000
    );
    const refreshTokenExpirationDate = new Date(
      this.getExpirationTime(refreshToken) * 1000
    );
    const id = this.getId(accessToken);
    const user = new User(
      email,
      id,
      accessToken,
      accessTokenExpirationDate,
      refreshToken,
      refreshTokenExpirationDate
    );
    this.user.next(user);
    logEvent(this.analytics, LogEventsEnum.auth_login);
    this.autoLogout(
      Math.max(
        this.getExpirationTime(accessToken) * 1000 - new Date().getTime(),
        0
      )
    );
    localStorage.setItem('userData', JSON.stringify(user));
  }

  logout() {
    // this.apollo.client.resetStore();
    this.fcmService.removeFCM().subscribe({
      next: (res) => {
        // console.log('remove fcm mutation subscription', res);
        this.store.dispatch(logout());
        this.apollo.client.clearStore();
        // this.socketService.disconnect();
        this.user.next(null);
        this.userService.clearUserData();
        this.socketService.disconnect();
        localStorage.removeItem('userData');
        localStorage.removeItem('userDetails');
        this.router.navigate(['/login']);
        if (this.tokenExpirationTimer) {
          clearTimeout(this.tokenExpirationTimer);
        }
        this.tokenExpirationTimer = null;
      },
      error: () => {
        // still do the following logout actions if removing fcm mutations doesn't work
        this.store.dispatch(logout());
        this.apollo.client.clearStore();
        // this.socketService.disconnect();
        this.user.next(null);
        this.userService.clearUserData();
        this.socketService.disconnect();
        localStorage.removeItem('userData');
        localStorage.removeItem('userDetails');
        this.router.navigate(['/login']);
        if (this.tokenExpirationTimer) {
          clearTimeout(this.tokenExpirationTimer);
        }
        this.tokenExpirationTimer = null;
      },
    });
  }

  autoLogin() {
    const userData: {
      email: string;
      id: string;
      _accessToken: string;
      _accessTokenExpirationDate: Date;
      _refreshToken: string;
      _refreshTokenExpirationDate: Date;
    } = JSON.parse(localStorage.getItem('userData'));
    if (!userData) {
      return;
    }
    const loadedUser = new User(
      userData.email,
      userData.id,
      userData._accessToken,
      new Date(userData._accessTokenExpirationDate),
      userData._refreshToken,
      new Date(userData._refreshTokenExpirationDate)
    );

    if (loadedUser.accessToken) {
      this.user.next(loadedUser);
      this.userService.fetchUserData();
      this.fcmService.registerTokenV3(loadedUser.id);
      const expirationDuration =
        new Date(userData._accessTokenExpirationDate).getTime() -
        new Date().getTime();
      this.autoLogout(Math.max(expirationDuration, 0));
    }
  }

  autoLogout(expirationDuration: number) {
    if (expirationDuration > 0) {
      this.tokenExpirationTimer = setTimeout(() => {
        this.refreshToken();
      }, expirationDuration);
    } else {
      this.refreshToken();
    }
  }

  // Resolves true when a fresh access token was stored. Timer-driven callers
  // ignore the result; the socket revocation flow awaits it before reconnecting.
  refreshToken(): Promise<boolean> {
    const userData: {
      email: string;
      id: string;
      _accessToken: string;
      _accessTokenExpirationDate: Date;
      _refreshToken: string;
      _refreshTokenExpirationDate: Date;
    } = JSON.parse(localStorage.getItem('userData'));
    if (!userData) {
      this.logout();
      return Promise.resolve(false);
    }
    const loadedUser = new User(
      userData.email,
      userData.id,
      userData._accessToken,
      new Date(userData._accessTokenExpirationDate),
      userData._refreshToken,
      new Date(userData._refreshTokenExpirationDate)
    );

    if (!loadedUser.refreshToken) {
      this.logout();
      return Promise.resolve(false);
    }

    return new Promise<boolean>((resolve) => {
      this.refreshTokenService.mutate().subscribe({
        next: (response) => {
          const loginResponse = response.data?.refreshToken;

          if (loginResponse && loginResponse.access_token) {
            const accessTokenExpirationDate = new Date(
              this.getExpirationTime(loginResponse.access_token) * 1000
            );
            const updatedUser = new User(
              userData.email,
              userData.id,
              loginResponse.access_token,
              accessTokenExpirationDate,
              userData._refreshToken,
              new Date(userData._refreshTokenExpirationDate)
            );
            this.user.next(updatedUser);
            this.autoLogout(
              Math.max(
                this.getExpirationTime(loginResponse.access_token) * 1000 -
                  new Date().getTime(),
                0
              )
            );
            localStorage.setItem('userData', JSON.stringify(updatedUser));
            resolve(true);
          } else {
            resolve(false);
          }
        },
        error: (error) => {
          console.error(
            'Error refreshing token:',
            JSON.stringify(error.message)
          );
          this.logout();
          resolve(false);
        },
      });
    });
  }

  // Specifically for mobile webview authentication.
  // Native mobile app injects its token as faqsAccessToken; falls back to userData if missing.
  checkMobileLocalStorageAuth(): boolean {
    try {
      // Try mobile-injected token first
      const faqsRaw = localStorage.getItem('faqsAccessToken');
      if (faqsRaw) {
        const faqsData = JSON.parse(faqsRaw);
        if (faqsData?._accessToken && this.validateToken(faqsData._accessToken)) {
          return true;
        }
      }

      // Fall back to web auth token
      const userRaw = localStorage.getItem('userData');
      if (userRaw) {
        const userData = JSON.parse(userRaw);
        if (userData?._accessToken && this.validateToken(userData._accessToken)) {
          return true;
        }
      }
    } catch (e) {
      console.warn('Failed to parse auth token from localStorage', e);
    }

    return false;
  }

  private validateToken(token: string): boolean {
    // Validates JWT format and expiration. Does NOT verify signature (requires backend public key).
    // This is for mobile webview auth only — main app auth uses server-side 401 validation.
    try {
      // Validate JWT format: should have 3 parts separated by dots
      const parts = token.split('.');
      if (parts.length !== 3) {
        return false;
      }

      const decodedToken = this.getExpirationTime(token);
      if (!decodedToken || typeof decodedToken !== 'number') {
        return false;
      }

      const accessTokenExpirationDate = new Date(decodedToken * 1000);
      const isNotExpired = accessTokenExpirationDate > new Date();

      return isNotExpired;
    } catch (e) {
      return false;
    }
  }

  signUpUser(data: object): Observable<string> {
    return this.apollo
      .mutate({
        mutation: signUpGQL,
        variables: { signUpInput: data },
      })
      .pipe(
        map((res) => {
          if (res.errors) {
            throw new Error("Couldn't sign up, error occurred");
          }
          return res.data?.['signUp']['_id'];
        })
      );
  }
}
